import React, { useState, useEffect, useRef } from 'react';
import { Accordion, Form, Alert } from 'react-bootstrap';
import StbButton from '../../../../atoms/button/StbButton';
import { Monteringsanalys } from '../../../../../api/generated/model';
import { CheckCircleTwoTone } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { isComplete, missingFields, type CompletionRule } from '../../../../../workPreparation/completionRule';
import './MonteringsanalysAccordionItem.css';
import { toastError } from '../../../../../utilities/messageUtils';

interface MonteringsanalysAccordionItemProps {
    projectId: number;
    projectInfo?: {
        projectNumber?: string;
        projectName?: string;
        customer?: { name?: string };
    };
    tableOfContentData?: {
        projectAddress?: string;
    };
    monteringsanalysData?: Monteringsanalys;
    workerView: boolean;
    managementView?: boolean;
    onSave: (data: Monteringsanalys) => Promise<void>;
    onSign?: (projectId: number, signedByName: string) => Promise<void>;
    available: boolean;
    completionRule?: CompletionRule;
}

const monteringsanalysFieldLabels: Partial<Record<keyof Monteringsanalys, string>> = {
    ritningOverensVerklighet: '1. Stämde ritning överens med verkligheten?',
    saknadesMaterialVidLeverans: '2. Fanns det saknat material vid leverans?',
    felpackatEllerFelavlastatMaterial: '3. Var materialet felpackat eller felavlastat?',
    underlattadePlanUppforande: '4. Underlättade planen för uppförande?',
    tillgangTillBodLunchrum: '5. Fanns det tillgång till bod/lunchrum på arbetsplatsen?',
    stallningOverlamnadGodkand: '6. Var ställningen överlämnad och godkänd (QR-kod)?',
    ataArbeteRegistrerat: '7. Har ÄTA-arbete registrerats i Byggkollen?',
    avvikelserRapporterats: '8. Har avvikelser rapporterats i Byggkollen?',
    beskrivProjektet: 'Beskriv projektet i sin helhet',
    taMedInforRivning: 'Ta med inför rivning',
};

const MonteringsanalysAccordionItem: React.FC<MonteringsanalysAccordionItemProps> = ({
    projectId,
    projectInfo,
    tableOfContentData,
    monteringsanalysData,
    workerView,
    managementView = false,
    onSave,
    onSign,
    available,
    completionRule,
}) => {
    const { instance } = useMsal();
    const [formData, setFormData] = useState<Monteringsanalys>({
        id: 0,
        projectId: projectId,
        projectNumber: projectInfo?.projectNumber ?? '',
        projectName: projectInfo?.projectName ?? '',
        customerName: projectInfo?.customer?.name ?? '',
        projectAddress: tableOfContentData?.projectAddress ?? '',
        ritningOverensVerklighet: null,
        saknadesMaterialVidLeverans: null,
        felpackatEllerFelavlastatMaterial: null,
        underlattadePlanUppforande: null,
        tillgangTillBodLunchrum: null,
        stallningOverlamnadGodkand: null,
        ataArbeteRegistrerat: null,
        avvikelserRapporterats: null,
        kommentarATAArbete: '',
        kommentarAvvikelser: '',
        beskrivProjektet: '',
        taMedInforRivning: '',
        isMonteringsanalysSigned: false,
        signedDate: null,
        signedBy: '',
        sharePointPdfUrl: ''
    });

    const [isSigning, setIsSigning] = useState(false);
    const signing = useRef(false);
    const [showSignError, setShowSignError] = useState(false);

    useEffect(() => {
        if (!monteringsanalysData) return;

        setFormData((prev) => ({
            ...prev,
            ...monteringsanalysData,
            projectId,
            projectNumber: monteringsanalysData.projectNumber ?? projectInfo?.projectNumber ?? prev.projectNumber ?? '',
            projectName: monteringsanalysData.projectName ?? projectInfo?.projectName ?? prev.projectName ?? '',
            customerName: monteringsanalysData.customerName ?? projectInfo?.customer?.name ?? prev.customerName ?? '',
            projectAddress: monteringsanalysData.projectAddress ?? tableOfContentData?.projectAddress ?? prev.projectAddress ?? '',
        }));

    }, [
        monteringsanalysData,
        projectId,
        projectInfo?.projectNumber,
        projectInfo?.projectName,
        projectInfo?.customer?.name,
        tableOfContentData?.projectAddress,
    ]);

    const handleCheckboxChange = (field: keyof Monteringsanalys, value: boolean | null) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleTextChange = (field: keyof Monteringsanalys, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSign = async () => {
        if (signing.current || formData.isMonteringsanalysSigned || !onSign) return;
        if (!formComplete) {
            setShowSignError(true);
            return;
        }
        setShowSignError(false);
        signing.current = true;
        setIsSigning(true);

        try {
            // Get user info from MSAL
            const activeAccount = instance.getActiveAccount();
            const userName = activeAccount?.name || 'Okänd användare';

            const dataToSave: Monteringsanalys = {
                ...formData,
                projectId,
                projectNumber: projectInfo?.projectNumber ?? formData.projectNumber ?? '',
                projectName: projectInfo?.projectName ?? formData.projectName ?? '',
                customerName: projectInfo?.customer?.name ?? formData.customerName ?? '',
                projectAddress: tableOfContentData?.projectAddress ?? formData.projectAddress ?? '',
            };

            await onSave(dataToSave);

            if (onSign) {
                await onSign(projectId, userName);
            }

        } catch (error) {
            toastError('Kunde inte spara eller signera monteringsanalysen', error);
        } finally {
            signing.current = false;
            setIsSigning(false);
        }
    };

    const formComplete = !!completionRule && isComplete(
        completionRule,
        formData as Record<string, unknown>,
    );
    const missingFieldPaths = completionRule
        ? missingFields(completionRule, formData as Record<string, unknown>)
        : [];
    const missingFieldLabels = missingFieldPaths.map((path) =>
        monteringsanalysFieldLabels[path as keyof Monteringsanalys] ?? path
    );

    const renderCheckboxField = (
        label: string,
        field: keyof Monteringsanalys,
        commentField?: keyof Monteringsanalys,
        commentLabel?: string
    ) => {
        const value = formData[field] as boolean | null;

        return (
            <div className={`ma-question ${showSignError && value == null ? 'ma-question-missing' : ''}`}>
                <div className="ma-question-row">
                    <Form.Label className="ma-label">{label}</Form.Label>
                    <div className="ma-checks">
                        <Form.Check
                            id={`${field}-yes-${projectId}`}
                            type="checkbox"
                            label="JA"
                            className="ma-check"
                            checked={value === true}
                            onChange={() => handleCheckboxChange(field, true)}
                            disabled={isSigning || formData.isMonteringsanalysSigned}
                            name={`${field}-group`}
                        />
                        <Form.Check
                            id={`${field}-no-${projectId}`}
                            type="checkbox"
                            label="NEJ"
                            className="ma-check"
                            checked={value === false}
                            onChange={() => handleCheckboxChange(field, false)}
                            disabled={isSigning || formData.isMonteringsanalysSigned}
                            name={`${field}-group`}
                        />
                    </div>
                </div>

                {commentField && (
                    <Form.Group className="ma-comment-group">
                        <Form.Label className="ma-comment-label">{commentLabel}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            className="ma-control"
                            value={(formData[commentField] as string) ?? ''}
                            onChange={(e) => handleTextChange(commentField, e.target.value)}
                            disabled={isSigning || formData.isMonteringsanalysSigned}
                            placeholder="Frivillig kommentar..."
                        />
                    </Form.Group>
                )}
            </div>
        );
    };

    return (
        <Accordion.Item className="accordion-item shadow fw-bold monteringsanalys-item" eventKey="9">
            <Accordion.Header className={!available ? 'disabled-header' : ''}>
                Monteringsanalys
                {monteringsanalysData?.isComplete && (
                    <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: '25px', marginLeft: '10px' }} />
                )}
            </Accordion.Header>

            <Accordion.Body className={`monteringsanalys-body ${!available ? 'disabled-body' : ''}`}>
                {!available && (
                    <Alert variant="info" className="mb-0">
                        Monteringsanalysen blir tillgänglig när egenkontrollen är genomförd.
                    </Alert>
                )}
                {available && (
                <Form className="monteringsanalys-form">
                    <div className="ma-project-card">
                        <div><strong>Projektnummer:</strong> {projectInfo?.projectNumber ?? '-'}</div>
                        <div><strong>Projektnamn:</strong> {projectInfo?.projectName ?? '-'}</div>
                        <div><strong>Kund:</strong> {projectInfo?.customer?.name ?? '-'}</div>
                        <div><strong>Adress:</strong> {tableOfContentData?.projectAddress ?? '-'}</div>
                    </div>

                    <hr className="ma-divider" />

                    {renderCheckboxField('1. Stämde ritning överens med verkligheten?', 'ritningOverensVerklighet')}
                    {renderCheckboxField('2. Fanns det saknat material vid leverans?', 'saknadesMaterialVidLeverans')}
                    {renderCheckboxField('3. Var materialet felpackat eller felavlastat?', 'felpackatEllerFelavlastatMaterial')}
                    {renderCheckboxField('4. Underlättade planen för uppförande?', 'underlattadePlanUppforande')}
                    {renderCheckboxField('5. Fanns det tillgång till bod/lunchrum på arbetsplatsen?', 'tillgangTillBodLunchrum')}
                    {renderCheckboxField('6. Var ställningen överlämnad och godkänd (QR-kod)?', 'stallningOverlamnadGodkand')}
                    {renderCheckboxField('7. Har ÄTA-arbete registrerats i Byggkollen?', 'ataArbeteRegistrerat', 'kommentarATAArbete', 'Anmärkning:')}
                    {renderCheckboxField('8. Har avvikelser rapporterats i Byggkollen?', 'avvikelserRapporterats', 'kommentarAvvikelser', 'Anmärkning:')}

                    <hr className="ma-divider" />

                    <Form.Group className="mb-2">
                        <Form.Label className="ma-label">Beskriv projektet i sin helhet *</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            className="ma-control"
                            value={formData.beskrivProjektet ?? ''}
                            onChange={(e) => handleTextChange('beskrivProjektet', e.target.value)}
                            disabled={isSigning || formData.isMonteringsanalysSigned}
                            isInvalid={showSignError && missingFieldPaths.includes('beskrivProjektet')}
                            placeholder="Beskriv projektet, utmaningar, lösningar etc..."
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label className="ma-label">Ta med inför rivning *</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            className="ma-control"
                            value={formData.taMedInforRivning ?? ''}
                            onChange={(e) => handleTextChange('taMedInforRivning', e.target.value)}
                            disabled={isSigning || formData.isMonteringsanalysSigned}
                            isInvalid={showSignError && missingFieldPaths.includes('taMedInforRivning')}
                            placeholder="Vad ska tas med inför rivning..."
                            required
                        />
                    </Form.Group>

                    <div className="ma-sign-section">
                        {showSignError && !formComplete && (
                            <Alert variant="danger" role="alert">
                                {completionRule ? (
                                    <>
                                        <div>Besvara alla frågor med JA eller NEJ och fyll i de obligatoriska textfälten innan du signerar.</div>
                                        {missingFieldLabels.length > 0 && (
                                            <div className="pwc-validation-msg pwc-validation-error">
                                                Följande fält saknas: {missingFieldLabels.join(', ')}.
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    'Valideringen kunde inte laddas. Ladda om sidan innan du signerar.'
                                )}
                            </Alert>
                        )}
                        {!formData.isMonteringsanalysSigned && (workerView || managementView) ? (
                            <div className="stb-action-bar">
                                <StbButton
                                    variant="primary"
                                    disabled={isSigning || formData.isMonteringsanalysSigned || !onSign}
                                    loading={isSigning}
                                    onClick={handleSign}
                                >
                                    {isSigning ? 'Signerar…' : 'Signera monteringsanalys'}
                                </StbButton>
                            </div>
                        ) : null}

                        {formData.isMonteringsanalysSigned && (
                            <div className="ma-signed-info">
                                Signerad av: {formData.signedBy}
                                <br />
                                Datum: {formData.signedDate
                                    ? new Date(formData.signedDate).toLocaleDateString('sv-SE')
                                    : ''}
                            </div>
                        )}
                    </div>
                </Form>
                )}
            </Accordion.Body>
        </Accordion.Item>
    );
};

export default MonteringsanalysAccordionItem;
